import Box from "@mui/material/Box";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { MetadataForm, MetadataView } from "@/shared/components";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { useUpdateDataset } from "@/shared/domain/datasets/hooks/useUpdateDataset";
import { OrgRecord } from "@/shared/domain/orgs";
import { QueryTarget } from "@/shared/domain/query";
import { MetadataValueQueryKeys } from "@/shared/domain/query/hooks";

interface UpdateDatasetMetadataProps {
  currentOrg: OrgRecord | null;
  datasetId: string;
  metadata: Record<string, string>;
  onError?: (error: Error) => void;
}

export const UpdateDatasetMetadata: React.FC<UpdateDatasetMetadataProps> = ({
  currentOrg,
  datasetId,
  metadata,
  onError,
}) => {
  const updateDatasetMutation = useUpdateDataset();
  const queryClient = useQueryClient();

  const removeField = (key: string) => {
    updateDatasetMutation.mutate(
      {
        datasetId: datasetId,
        resourceOwnerId: currentOrg?.org_id || "",
        updates: {
          metadata_changeset: {
            remove_fields: [key],
          },
        },
      },
      {
        onError: (error: Error) => {
          if (onError) {
            onError(error);
          }
        },
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: MetadataValueQueryKeys.forKey(QueryTarget.Datasets, key),
          });
        },
      },
    );
  };

  const addFields = (updates: Record<string, unknown>) => {
    updateDatasetMutation.mutate(
      {
        datasetId: datasetId,
        resourceOwnerId: currentOrg?.org_id || "",
        updates: {
          metadata_changeset: {
            put_fields: updates,
          },
        },
      },
      {
        onError: (error: Error) => {
          if (onError) {
            onError(error);
          }
        },
        onSuccess: () => {
          Object.keys(updates).forEach((key) => {
            void queryClient.invalidateQueries({
              queryKey: MetadataValueQueryKeys.forKey(
                QueryTarget.Datasets,
                key,
              ),
            });
          });
        },
      },
    );
  };

  return (
    <Box>
      <MetadataView
        metadata={metadata}
        onChange={(metadata: Record<string, unknown>, diff) => {
          if (diff?.keysDeleted && diff.keysDeleted.length > 0) {
            diff.keysDeleted.map(removeField);
          }
          if (diff?.keysUpdated && diff.keysUpdated.length > 0) {
            const putFields: Record<string, unknown> = {};
            for (const k of diff.keysUpdated) {
              putFields[k] = metadata[k];
            }
            addFields(putFields);
          }
        }}
      />
      <MetadataForm
        metadata={metadata}
        autocompleteType={AutocompleteType.ActionMetadataKeys}
        onAddMetadata={(key, value) => addFields({ [key]: value })}
      />
    </Box>
  );
};
